/**
 * Reviews.io widgets, ported from the main shop (Valkental/shop-theme: `dev/src/js/components/reviews-io-product-rating.js`,
 * `snippets/reviewsio-lazy-loader.liquid`, `sections/product-reviews-io-widget.liquid`,
 * `snippets/reviewsio-floating-widget.liquid`). All load straight from Reviews.io for the account in the theme setting
 * `reviews_io_store`; product widgets find a product's reviews by its SKUs.
 * - `<reviews-io-rating>` (blocks/reviews-io-rating.liquid): stars + review count from the rating snippet.
 * - `<reviews-io-widget>` (sections/reviews-io.liquid): the product reviews widget (Polaris), loaded once its section
 *   comes within 400 px of the viewport.
 * - `<reviews-io-floating>` (sections/reviews-io-floating.liquid): the floating badge with the shop's review count,
 *   loaded on the visitor's first interaction.
 * - `<reviews-io-copy>` (sections/reviews-io.liquid): the English copy of the product's reviews from
 *   `assets/reviews-io-en.json`, shown instead of Polaris' German list.
 */

const RATING_SNIPPET_SCRIPT = 'https://widget.reviews.io/rating-snippet/dist.js';
const RATING_SNIPPET_STYLES = 'https://widget.reviews.io/rating-snippet/dist.css';
const POLARIS_SCRIPT = 'https://widget.reviews.io/polaris/build.js';
const FLOATING_SCRIPT = 'https://widget.reviews.io/modern-widgets/floating.js';

/**
 * Globals the Reviews.io scripts define.
 *
 * @typedef {Window & {
 *   ratingSnippet?: (className: string, options: Record<string, unknown>) => void,
 *   ReviewsWidget?: new (selector: string, config: Record<string, unknown>) => unknown,
 * }} ReviewsIoWindow
 */
const reviewsIo = /** @type {ReviewsIoWindow} */ (window);

/** @type {Map<string, Promise<void>>} */
const scripts = new Map();

/**
 * Adds a classic script to the page once; later calls wait for the same load.
 *
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  let loading = scripts.get(src);
  if (loading) return loading;

  loading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => {
        scripts.delete(src);
        script.remove();
        reject(new Error(`Could not load ${src}`));
      },
      { once: true }
    );
    document.head.append(script);
  });
  scripts.set(src, loading);
  return loading;
}

/** @param {string} href */
function loadStylesheet(href) {
  if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.append(link);
}

/**
 * Stars and review count of a product's SKUs, drawn by Reviews.io's rating snippet into `.ruk_rating_snippet`.
 * The snippet labels itself ("4.75 Stars - 117 reviews"); the link around it takes that label over. Without reviews
 * the snippet marks itself `aria-hidden` and the block hides.
 */
class ReviewsIoRating extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    const snippet = this.querySelector('.ruk_rating_snippet');
    if (!snippet) return;

    this.#observer = new MutationObserver(() => this.#onSnippetUpdate(snippet));
    this.#observer.observe(snippet, { attributeFilter: ['data-retrieved', 'aria-hidden'] });

    loadStylesheet(RATING_SNIPPET_STYLES);
    loadScript(RATING_SNIPPET_SCRIPT).then(
      () => this.#render(),
      () => (this.hidden = true)
    );
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #render() {
    const { store, lang, text, singularText } = this.dataset;

    // Fills every rating snippet on the page that isn't filled yet, in one request. `color` and `textClr` would
    // otherwise be inline green and grey; `inherit` leaves the colours to the block's CSS.
    reviewsIo.ratingSnippet?.('ruk_rating_snippet', {
      store,
      lang,
      mode: 'default',
      color: 'inherit',
      textClr: 'inherit',
      linebreak: false,
      text,
      singularText,
      formatReviewCount: true,
      formatLanguage: document.documentElement.lang || 'en',
      usePolaris: false,
      showEmptyStars: false,
    });
  }

  /** @param {Element} snippet */
  #onSnippetUpdate(snippet) {
    if (snippet.getAttribute('aria-hidden') === 'true') {
      this.hidden = true;
    } else if (snippet.hasAttribute('data-retrieved')) {
      const label = snippet.getAttribute('aria-label');
      if (label) this.querySelector('a')?.setAttribute('aria-label', label);
    } else {
      return;
    }

    this.#observer?.disconnect();
  }
}

/**
 * Polaris styles in the theme's colours, fonts and button shapes; the values resolve against the section's CSS
 * variables, so a section background colour carries over.
 */
const POLARIS_STYLES = {
  '--base-font-size': '16px',

  '--common-button-font-family': 'var(--font-body--family)',
  '--common-button-font-size': '15px',
  '--common-button-font-weight': '600',
  '--common-button-letter-spacing': '0',
  '--common-button-text-transform': 'none',
  '--common-button-vertical-padding': '15px',
  '--common-button-horizontal-padding': '26px',
  '--common-button-border-width': '1.5px',
  '--common-button-border-radius': 'var(--style-border-radius-buttons-primary)',

  '--primary-button-bg-color': 'var(--color-foreground)',
  '--primary-button-border-color': 'var(--color-foreground)',
  '--primary-button-text-color': 'var(--color-background)',

  '--secondary-button-bg-color': 'transparent',
  '--secondary-button-border-color': 'var(--color-foreground)',
  '--secondary-button-text-color': 'var(--color-foreground)',

  '--common-star-color': 'var(--color-foreground)',
  '--common-star-disabled-color': 'rgb(var(--color-foreground-rgb) / 0.2)',
  '--medium-star-size': '20px',
  '--small-star-size': '16px',

  '--heading-text-color': 'var(--color-foreground)',
  '--heading-text-font-weight': '700',
  '--heading-text-font-family': 'var(--font-heading--family)',
  '--heading-text-line-height': '1.2',
  '--heading-text-letter-spacing': '-0.01em',
  '--heading-text-transform': 'none',

  '--body-text-color': 'var(--color-foreground)',
  '--body-text-font-weight': '400',
  '--body-text-font-family': 'var(--font-body--family)',
  '--body-text-line-height': '1.6',
  '--body-text-letter-spacing': '0',
  '--body-text-transform': 'none',

  '--inputfield-text-font-family': 'var(--font-body--family)',
  '--input-text-font-size': '15px',
  '--inputfield-text-font-weight': '400',
  '--inputfield-text-color': 'var(--color-foreground)',
  '--inputfield-border-color': 'rgb(var(--color-foreground-rgb) / 0.2)',
  '--inputfield-background-color': 'var(--palette-lightest)',
  '--inputfield-border-width': '1px',
  '--inputfield-border-radius': 'var(--style-border-radius-inputs)',

  '--common-border-color': 'rgb(var(--color-foreground-rgb) / 0.12)',
  '--common-border-width': '1px',
  '--common-sidebar-width': '170px',

  '--slider-indicator-bg-color': 'rgb(var(--color-foreground-rgb) / 0.12)',
  '--slider-indicator-button-color': 'var(--color-foreground)',
  '--slider-indicator-width': '170px',

  '--badge-icon-color': 'var(--color-foreground)',
  '--badge-icon-font-size': 'inherit',
  '--badge-text-color': 'var(--color-foreground)',
  '--badge-text-font-size': 'inherit',
  '--badge-text-letter-spacing': 'inherit',
  '--badge-text-transform': 'inherit',

  '--author-font-size': 'inherit',
  '--author-text-transform': 'none',

  '--avatar-thumbnail-size': '48px',
  '--avatar-thumbnail-border-radius': '100px',
  '--avatar-thumbnail-text-color': 'var(--color-foreground)',
  '--avatar-thumbnail-bg-color': 'var(--color-background)',

  '--photo-video-thumbnail-size': '80px',
  '--photo-video-thumbnail-border-radius': '10px',

  '--pagination-tab-text-color': 'var(--color-foreground)',
  '--pagination-tab-text-transform': 'none',
  '--pagination-tab-text-letter-spacing': '0',
  '--pagination-tab-text-font-size': '15px',
  '--pagination-tab-text-font-weight': '600',
  '--pagination-tab-active-text-color': 'var(--color-foreground)',
  '--pagination-tab-active-text-font-weight': '600',
  '--pagination-tab-active-border-color': 'var(--color-foreground)',
  '--pagination-tab-border-width': '2px',
};

/**
 * Widget options as on the main shop's PDP, except: reviews as cards (`bordered`, filled white by the section's CSS),
 * no product name on every review (it repeats the main shop's German product title), and the summary with "Write a
 * review" stays for products without reviews yet.
 *
 * @param {DOMStringMap} data
 * @returns {Record<string, unknown>}
 */
function polarisConfig({ store, sku, grouping, lang, perPage, questions }) {
  const withQuestions = questions === 'true';

  return {
    store,
    widget: 'polaris',
    options: {
      types: withQuestions ? 'product_review,questions' : 'product_review',
      enable_sentiment_analysis: true,
      lang,
      layout: 'bordered',
      per_page: Number(perPage) || 5,
      store_review: { hide_if_no_results: true },
      third_party_review: { hide_if_no_results: true },
      product_review: { sku, enable_rich_snippets: false },
      questions: {
        hide_if_no_results: true,
        enable_ask_question: withQuestions,
        enable_ask_question_button_style: false,
        show_dates: true,
        grouping,
      },
      header: {
        enable_summary: true,
        enable_ratings: true,
        enable_attributes: true,
        enable_image_gallery: false,
        enable_percent_recommended: false,
        enable_write_review: true,
        enable_ask_question: withQuestions,
        enable_sub_header: false,
        rating_decimal_places: 2,
        use_write_review_button: false,
        enable_if_no_results: true,
      },
      filtering: {
        enable: false,
        enable_text_search: false,
        enable_sorting: false,
        enable_product_filter: false,
        enable_media_filter: false,
        enable_overall_rating_filter: false,
        enable_language_filter: false,
        enable_language_filter_language_change: false,
        enable_ratings_filters: false,
        enable_attributes_filters: false,
        enable_expanded_filters: false,
      },
      reviews: {
        enable_avatar: true,
        enable_reviewer_name: true,
        enable_reviewer_address: true,
        reviewer_address_format: 'city, country',
        enable_verified_badge: true,
        enable_subscriber_badge: true,
        enable_reviewer_recommends: true,
        enable_attributes: true,
        enable_product_name: false,
        enable_replies: true,
        enable_images: true,
        enable_ratings: true,
        enable_share: true,
        enable_helpful_vote: true,
        enable_helpful_display: true,
        enable_report: true,
        enable_date: true,
        enable_third_party_source: true,
      },
    },
    styles: POLARIS_STYLES,
  };
}

/**
 * Reviews.io's product reviews widget (summary, "Write a review", reviews, questions) for the SKUs in `data-sku`.
 * Polaris (~100 KB) loads once the element comes within 400 px of the viewport. If it can't load (blocked or offline),
 * the whole section hides instead of leaving its heading above an empty column.
 */
class ReviewsIoWidget extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer?.disconnect();
        this.#mount();
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #mount() {
    const target = this.querySelector('[data-reviews-io-mount]');
    if (!target?.id) return;

    try {
      await loadScript(POLARIS_SCRIPT);
    } catch {
      const section = this.closest('.reviews-io');
      if (section instanceof HTMLElement) section.hidden = true;
      return;
    }

    if (!this.isConnected || !reviewsIo.ReviewsWidget) return;
    new reviewsIo.ReviewsWidget(`#${target.id}`, polarisConfig(this.dataset));
  }
}

/**
 * First interactions that load the floating badge. `scroll` is listened for in the capture phase instead (below):
 * on desktop the theme scrolls `.page-wrapper`, not the window, and element scroll events don't bubble.
 */
const INTERACTIONS = /** @type {const} */ (['pointermove', 'pointerdown', 'touchstart', 'wheel', 'keydown']);

/**
 * Reviews.io's floating badge (stars, review count, a panel with the latest reviews). The script (~190 KB) loads on
 * the visitor's first scroll, tap, mouse move or key press, like on the main shop, and mounts into the
 * `#reviews-io-floating-widget` element it finds at that moment, reading its `widget-id`, `data-store-name` and `lang`.
 */
class ReviewsIoFloating extends HTMLElement {
  connectedCallback() {
    for (const type of INTERACTIONS) window.addEventListener(type, this.#load, { passive: true });
    document.addEventListener('scroll', this.#load, { passive: true, capture: true });
  }

  disconnectedCallback() {
    this.#stopListening();
  }

  #load = () => {
    this.#stopListening();
    loadScript(FLOATING_SCRIPT).catch(() => (this.hidden = true));
  };

  #stopListening() {
    for (const type of INTERACTIONS) window.removeEventListener(type, this.#load);
    document.removeEventListener('scroll', this.#load, { capture: true });
  }
}

/**
 * A review in `assets/reviews-io-en.json`: Reviews.io's data with text and replies translated from German.
 *
 * @typedef {{ id: number, sku: string, rating: number, date: string, author: string, location: string,
 *   verified: boolean, text: string, replies: string[] }} CopiedReview
 */

/**
 * Page numbers as Polaris picks them (`_getChips` in widget.reviews.io/polaris/build.js): up to `shown` chips around
 * the current page, with the first/last page behind a gap once there are more pages than chips; `null` is the gap.
 * Page 1 of 18 with 12 chips: 1 2 3 4 5 6 7 8 9 10 … 18.
 *
 * @param {number} current
 * @param {number} total
 * @param {number} shown
 * @returns {(number | null)[]}
 */
function pageChips(current, total, shown) {
  /** @param {number} from @param {number} to */
  const pages = (from, to) => Array.from({ length: Math.max(0, to - from + 1) }, (_, index) => from + index);
  const size = Math.min(shown, 12);
  let start = Math.max(1, current - Math.floor(size / 2));
  let end = start + size - 1;
  if (end > total) {
    end = total;
    start = Math.max(1, end - size + 1);
  }

  if (size < 7 || size >= total) return pages(start, end);
  if (current <= size - 3) return [...pages(1, size - 2), ...(end < total ? [null, total] : [])];
  if (current >= total - size + 4) return [...(start > 1 ? [1, null] : []), ...pages(total - size + 3, total)];

  start += 2;
  end -= 2;
  return [...(start > 1 ? [1, null] : []), ...pages(start, end), ...(end < total ? [null, total] : [])];
}

/**
 * Polaris' "3 days ago" style for a review date.
 *
 * @param {Date} date
 * @param {Intl.RelativeTimeFormat} format
 */
function timeAgo(date, format) {
  const days = Math.max(0, Math.round((Date.now() - date.getTime()) / 86_400_000));
  if (days < 7) return format.format(-days, 'day');
  if (days < 30) return format.format(-Math.floor(days / 7), 'week');
  if (days < 365) return format.format(-Math.floor(days / 30), 'month');
  return format.format(-Math.floor(days / 365), 'year');
}

/**
 * English copy of the product's reviews, laid out like Polaris' list: the reviews with text for the SKUs in
 * `data-sku`, newest first, from the JSON in `data-src`, filled into the section's `<template>` card, `data-per-page`
 * per page with Polaris' page numbers. Loads once the element comes within 400 px of the viewport. Choosing a page
 * scrolls the list into view and moves focus to its first review. Hides itself when the file can't be read or holds
 * no review for these SKUs.
 */
class ReviewsIoCopy extends HTMLElement {
  /** @type {CopiedReview[]} */
  #reviews = [];

  #page = 1;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  #relativeFormat = new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', { numeric: 'auto' });

  connectedCallback() {
    this.querySelector('[data-pages]')?.addEventListener('click', this.#onPageClick);
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.querySelector('[data-pages]')?.removeEventListener('click', this.#onPageClick);
  }

  get #perPage() {
    return Number(this.dataset.perPage) || 5;
  }

  async #load() {
    const { src, sku } = this.dataset;
    if (!src || !sku) return;

    const skus = new Set(sku.split(';'));
    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`${src}: ${response.status}`);
      const data = /** @type {{ reviews: CopiedReview[] }} */ (await response.json());
      this.#reviews = data.reviews.filter((review) => skus.has(review.sku));
    } catch {
      this.#reviews = [];
    }

    if (this.#reviews.length === 0) {
      this.hidden = true;
      return;
    }

    this.#showPage(1);

    // Polaris fits as many page numbers as the pagination row has room for (50 px each).
    const row = this.querySelector('[data-pages]')?.parentElement;
    if (row) {
      this.#resizeObserver = new ResizeObserver(() => this.#renderPages());
      this.#resizeObserver.observe(row);
    }
  }

  /** @param {Event} event */
  #onPageClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-page]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const page = Number(button.dataset.page);
    if (!page || page === this.#page) return;

    this.#showPage(page);
    this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    const card = this.querySelector('[data-list] article');
    if (card instanceof HTMLElement) {
      card.tabIndex = -1;
      card.focus({ preventScroll: true });
    }
  };

  /** @param {number} page */
  #showPage(page) {
    const list = this.querySelector('[data-list]');
    const template = this.querySelector('template');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const from = (page - 1) * this.#perPage;
    list.replaceChildren(...this.#reviews.slice(from, from + this.#perPage).map((review) => this.#card(template, review)));
    this.#page = page;
    this.#renderPages();
  }

  #renderPages() {
    const container = this.querySelector('[data-pages]');
    if (!(container instanceof HTMLElement)) return;

    const total = Math.ceil(this.#reviews.length / this.#perPage);
    if (total < 2) {
      container.replaceChildren();
      return;
    }

    const fitting = Math.round((container.parentElement ?? container).clientWidth / 50);
    const shown = fitting < 5 ? 4 : Math.min(fitting, 12);
    const label = container.dataset.label ?? '%PAGE%';
    container.replaceChildren(
      ...pageChips(this.#page, total, shown).map((page) => {
        if (page === null) {
          const gap = document.createElement('span');
          gap.className = 'reviews-io-copy__gap';
          gap.setAttribute('aria-hidden', 'true');
          gap.textContent = '...';
          return gap;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'reviews-io-copy__page';
        button.dataset.page = String(page);
        button.textContent = String(page);
        button.setAttribute('aria-label', label.replace('%PAGE%', String(page)));
        if (page === this.#page) button.setAttribute('aria-current', 'page');
        return button;
      })
    );
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {CopiedReview} review
   * @returns {HTMLElement}
   */
  #card(template, review) {
    const card = /** @type {HTMLElement} */ (template.content.firstElementChild?.cloneNode(true));
    /** @param {string} name */
    const field = (name) => card.querySelector(`[data-field="${name}"]`);

    const initials = field('initials');
    if (initials) {
      const [first = '', second = ''] = review.author.split(/\s+/);
      initials.textContent = `${first.charAt(0).toUpperCase()}${second.charAt(0)}`;
    }

    const verified = field('verified');
    if (verified instanceof HTMLElement) verified.hidden = !review.verified;

    const author = field('author');
    if (author) author.textContent = review.author;

    const location = field('location');
    if (location) location.textContent = review.location;

    const stars = field('stars');
    if (stars instanceof HTMLElement) {
      stars.setAttribute('aria-label', (stars.dataset.label ?? '').replace('%RATING%', String(review.rating)));
      stars.querySelectorAll('svg').forEach((star, index) => star.classList.toggle('is-filled', index < review.rating));
    }

    const text = field('text');
    if (text) text.textContent = review.text;

    const reply = field('reply');
    const replyText = field('reply-text');
    if (reply instanceof HTMLElement && replyText && review.replies.length > 0) {
      replyText.textContent = review.replies.join('\n\n');
      reply.hidden = false;
    }

    const date = field('date');
    if (date) {
      const day = new Date(`${review.date}T12:00:00`);
      date.setAttribute('datetime', review.date);
      date.setAttribute('title', this.#dateFormat.format(day));
      date.textContent = timeAgo(day, this.#relativeFormat);
    }

    return card;
  }
}

if (!customElements.get('reviews-io-rating')) {
  customElements.define('reviews-io-rating', ReviewsIoRating);
}

if (!customElements.get('reviews-io-widget')) {
  customElements.define('reviews-io-widget', ReviewsIoWidget);
}

if (!customElements.get('reviews-io-floating')) {
  customElements.define('reviews-io-floating', ReviewsIoFloating);
}

if (!customElements.get('reviews-io-copy')) {
  customElements.define('reviews-io-copy', ReviewsIoCopy);
}
